if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Apply initial variant filtering
        this.applyInitialVariantFilter();
        
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      applyInitialVariantFilter() {
        // Get the current variant title from the product info section
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        const variantIdInput = productInfo.querySelector('variant-selects [data-selected-variant]');
        if (!variantIdInput) return;

        try {
          const variantData = JSON.parse(variantIdInput.innerHTML);
          if (variantData?.title) {
            this.filterMediaByVariant(variantData.title);
          }
        } catch (e) {
          console.error('Error parsing initial variant data:', e);
        }
      }

      filterMediaByVariant(variantTitle) {
        if (!variantTitle) return;

        // Normalize the variant title for comparison (lowercase, trim)
        const normalizedVariantTitle = variantTitle.toLowerCase().trim();

        // Get all media items in both the viewer and thumbnails
        const viewerItems = this.elements.viewer.querySelectorAll('[data-media-id]');
        const thumbnailItems = this.elements.thumbnails?.querySelectorAll('[data-target]') || [];

        // Filter viewer items
        viewerItems.forEach((item) => {
          const filterValue = item.dataset.variantFilter;
          if (filterValue) {
            // Item has a variant filter - only show if it matches
            if (filterValue === normalizedVariantTitle) {
              item.style.display = '';
              item.classList.remove('hidden');
            } else {
              item.style.display = 'none';
              item.classList.add('hidden');
            }
          } else {
            // No filter - always show
            item.style.display = '';
            item.classList.remove('hidden');
          }
        });

        // Filter thumbnail items
        thumbnailItems.forEach((item) => {
          const filterValue = item.dataset.variantFilter;
          if (filterValue) {
            // Item has a variant filter - only show if it matches
            if (filterValue === normalizedVariantTitle) {
              item.style.display = '';
              item.classList.remove('hidden');
            } else {
              item.style.display = 'none';
              item.classList.add('hidden');
            }
          } else {
            // No filter - always show
            item.style.display = '';
            item.classList.remove('hidden');
          }
        });

        // Update the slider counter to reflect visible items
        this.updateSliderCounter();

        // Ensure the first visible item is active
        this.setFirstVisibleAsActive();

        // Reset the sliders so they recalculate visible items and navigation buttons
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          if (this.elements.viewer?.resetPages) {
            this.elements.viewer.resetPages();
          }
          if (this.elements.thumbnails?.resetPages) {
            this.elements.thumbnails.resetPages();
          }
          
          // Force an update on the sliders
          if (this.elements.viewer?.update) {
            this.elements.viewer.update();
          }
          if (this.elements.thumbnails?.update) {
            this.elements.thumbnails.update();
          }
        });
      }

      updateSliderCounter() {
        const counterTotal = this.querySelector('.slider-counter--total');
        if (counterTotal) {
          const visibleItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter(
            (item) => item.style.display !== 'none'
          );
          counterTotal.textContent = visibleItems.length;
        }
      }

      setFirstVisibleAsActive() {
        const visibleItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter(
          (item) => item.style.display !== 'none'
        );
        
        if (visibleItems.length > 0) {
          // Remove active class from all items
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
            item.classList.remove('is-active');
          });
          
          // Set first visible item as active
          const firstVisible = visibleItems[0];
          firstVisible.classList.add('is-active');
          
          // Update thumbnail
          if (this.elements.thumbnails) {
            const mediaId = firstVisible.dataset.mediaId;
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            this.setActiveThumbnail(activeThumbnail);
          }
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
